/**
 * `useT()` — the chrome's words, and the language the whole console is in.
 *
 * The language lives in this store rather than in a context, for the same reason §3.3 gives: a
 * context provider re-renders its whole subtree, and the strip under it takes a quote batch every
 * second.
 */

import { create } from "zustand";
import { en, type Keys } from "./en";
import { zhHant } from "./zh-Hant";
import { TOGGLE_LABEL, applyLang, applyTheme, savedLang, savedTheme, showing, type Lang, type Theme } from "./boot";

const DICTS: Record<Lang, Record<Keys, string>> = { en, "zh-Hant": zhHant };

interface UiState {
  lang: Lang;
  /** What is on screen. `null` means "follow the system", which is the default and is dark-first. */
  theme: Theme | null;
  setLang: (lang: Lang) => void;
  toggleTheme: () => void;
}

export const useUi = create<UiState>((set) => ({
  lang: savedLang(),
  theme: savedTheme(),
  setLang: (lang) => {
    applyLang(lang);
    set({ lang });
  },
  toggleTheme: () => {
    const next: Theme = showing() === "dark" ? "light" : "dark";
    applyTheme(next);
    set({ theme: next });
  },
}));

/**
 * One key → one string, with `{}` placeholders filled. Interpolation is why this is a dictionary
 * and not the mockups' dual-DOM: there is nowhere in duplicated markup to put a number.
 */
export function useT() {
  const lang = useUi((s) => s.lang);
  const dict = DICTS[lang];
  const t = (key: Keys, fill?: Record<string, string | number>): string => {
    const said = dict[key];
    if (!fill) return said;
    return Object.entries(fill).reduce(
      (out, [name, value]) => out.replace(`{${name}}`, String(value)),
      said,
    );
  };
  return { t, lang };
}

/** The theme toggle's bilingual label, which names the act rather than the state. */
export function themeLabel(lang: Lang): string {
  return TOGGLE_LABEL[lang][showing() === "dark" ? "light" : "dark"];
}

export type { Lang, Theme };
